import { Phone, MessageCircle, Mail, MapPin, Clock } from "lucide-react";
import { Reveal } from "@/components/motion";
import LeadForm from "@/components/LeadForm";
import { CONTACT } from "@/data/site";

const quickCards = [
  { icon: Phone, label: "Call us", value: CONTACT.phone, href: `tel:${CONTACT.phoneRaw}`, testid: "quick-call" },
  { icon: MessageCircle, label: "WhatsApp", value: "Chat instantly", href: CONTACT.whatsapp, testid: "quick-whatsapp" },
  { icon: Mail, label: "Email us", value: CONTACT.email, href: `mailto:${CONTACT.email}`, testid: "quick-email" },
];

export default function ContactSection({ showMap = true }) {
  return (
    <section className="dq-container py-20 lg:py-28" data-testid="contact-section" id="contact">
      <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:gap-14">
        {/* Left info */}
        <div>
          <Reveal><p className="label-eyebrow mb-4">Get in touch</p></Reveal>
          <Reveal delay={0.06}>
            <h2 className="font-display text-3xl font-extrabold leading-[1.1] tracking-tighter text-dq-ink sm:text-4xl lg:text-[2.75rem]">
              Let&rsquo;s grow your business, <span className="gradient-text">together</span>.
            </h2>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="mt-4 text-lg leading-relaxed text-dq-muted">
              Book a free consultation and we&rsquo;ll build a growth plan tailored to your business.
            </p>
          </Reveal>

          <div className="mt-8 space-y-3">
            {quickCards.map((c) => (
              <a key={c.label} href={c.href} target={c.href.startsWith("http") ? "_blank" : undefined} rel="noreferrer"
                data-testid={c.testid}
                className="group flex items-center gap-4 rounded-2xl border border-dq-line bg-white p-4 transition-[transform,border-color] duration-300 hover:-translate-y-0.5 hover:border-dq-orange/40">
                <span className="grid h-12 w-12 place-items-center rounded-xl bg-dq-orange/10 text-dq-orange transition-transform duration-300 group-hover:scale-110">
                  <c.icon className="h-5 w-5" />
                </span>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-dq-muted">{c.label}</div>
                  <div className="font-display font-bold text-dq-ink">{c.value}</div>
                </div>
              </a>
            ))}
          </div>

          <div className="mt-4 flex items-center gap-6 rounded-2xl border border-dq-line bg-white p-4 text-sm text-dq-muted">
            <span className="flex items-center gap-2"><MapPin className="h-4 w-4 text-dq-orange" /> {CONTACT.city}</span>
            <span className="flex items-center gap-2"><Clock className="h-4 w-4 text-dq-orange" /> Mon–Sat, 10am–7pm</span>
          </div>

          {showMap && (
            <div className="mt-4 overflow-hidden rounded-2xl border border-dq-line" data-testid="map-placeholder">
              <iframe
                title="DiziTeQ location — Bengaluru"
                src="https://www.google.com/maps?q=Bengaluru,Karnataka&output=embed"
                className="h-56 w-full grayscale-[0.2]"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          )}
        </div>

        {/* Right form */}
        <Reveal delay={0.1}>
          <div className="rounded-[2rem] border border-dq-line bg-white p-6 dq-shadow-soft sm:p-8">
            <h3 className="font-display text-xl font-bold text-dq-ink">Request your free consultation</h3>
            <p className="mt-1 text-sm text-dq-muted">Fill this out and we&rsquo;ll get back within 24 hours.</p>
            <div className="mt-6">
              <LeadForm />
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
